import React, { useState, useEffect, useRef } from 'react'
import {
  Bars3Icon,
  HomeIcon,
  UsersIcon,
  XMarkIcon,
  TruckIcon,
  DocumentTextIcon,
  UserCircleIcon,
  ArrowLeftIcon,
  ShieldCheckIcon,
} from '@heroicons/react/24/outline'
import { NavLink, useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'
import { COMPANY } from '@/config/company'
import NotificationPanel from './NotificationPanel'
import logo from '../assets/logo.png';

const navigation = [
  { name: 'Dashboard', href: '/dashboard', icon: HomeIcon },
  { name: 'Customers', href: '/customers', icon: UsersIcon },
  { name: 'Shipments', href: '/shipments', icon: TruckIcon },
  { name: 'Invoices', href: '/invoices', icon: DocumentTextIcon },
]

// Superadmin-only. This page existed but wasn't linked from anywhere, so it was
// reachable only by typing the URL.
const superadminNavigation = [
  { name: 'Users', href: '/users', icon: ShieldCheckIcon },
]

/** Title for the current route, so the topbar stops saying "Admin Dashboard" everywhere. */
function pageTitle(pathname: string): string {
  if (pathname.startsWith('/customers/new')) return 'New Customer'
  if (pathname.startsWith('/customers/')) return 'Customer Details'
  if (pathname.startsWith('/customers')) return 'Customers'
  if (pathname.startsWith('/shipments/new')) return 'New Shipment'
  if (pathname.startsWith('/shipments/')) return 'Shipment Details'
  if (pathname.startsWith('/shipments')) return 'Shipments'
  if (pathname.startsWith('/invoices/new')) return 'New Invoice'
  if (pathname.startsWith('/invoices/')) return 'Invoice Details'
  if (pathname.startsWith('/invoices')) return 'Invoices'
  if (pathname.startsWith('/users')) return 'User Management'
  if (pathname.startsWith('/profile')) return 'Profile'
  return 'Dashboard'
}

/** Detail routes get a Back control; the list they came from is the target. */
function backTarget(pathname: string): { label: string; href: string } | null {
  const detail = /^\/(customers|shipments|invoices)\/(?!new$)[^/]+$/.exec(pathname)
  if (!detail) return null
  const section = detail[1]
  const labels: Record<string, string> = {
    customers: 'Back to Customers',
    shipments: 'Back to Shipments',
    invoices: 'Back to Invoices',
  }
  return { label: labels[section], href: `/${section}` }
}

function classNames(...classes: string[]) {
  return classes.filter(Boolean).join(' ')
}

interface AdminLayoutProps {
  children?: React.ReactNode;
}

const AdminLayout: React.FC<AdminLayoutProps> = ({ children }) => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(true)
  const { user, logout } = useAuth()
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const location = useLocation()
  const navigate = useNavigate()

  const handleLogout = () => {
    logout();
  };

  const activeLink = 'bg-blue-100 text-blue-700 border-l-4 border-blue-600';
  const normalLink = 'hover:bg-blue-50 hover:text-blue-700';

  const title = pageTitle(location.pathname);
  const back = backTarget(location.pathname);
  const navItems = user?.role === 'superadmin'
    ? [...navigation, ...superadminNavigation]
    : navigation;

  // Close the profile dropdown on an outside click — it used to stay open until
  // the button was pressed again.
  const dropdownRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!dropdownOpen) return;
    const onPointerDown = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setDropdownOpen(false);
      }
    };
    const onEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setDropdownOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onEscape);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onEscape);
    };
  }, [dropdownOpen]);

  // Close it on navigation too.
  useEffect(() => {
    setDropdownOpen(false);
  }, [location.pathname]);

  return (
    <div className="flex h-screen bg-gray-50 font-sans">
      {/* Sidebar */}
      <aside className={`bg-white border-r ${isSidebarOpen ? 'w-64' : 'w-20'} transition-all duration-300 ease-in-out flex flex-col`}>
        <div className="p-6 flex items-center space-x-2">
          <img src={logo} className="h-8 w-8" alt="Logo" />
          <span className={`font-bold text-lg text-blue-700 transition-all duration-300 ${isSidebarOpen ? 'block' : 'hidden'}`}>{COMPANY.shortName}</span>
        </div>
        <nav className="flex-1 px-2 space-y-1">
          {navItems.map((item) => (
            <NavLink
              key={item.name}
              to={item.href}
              className={({ isActive }) =>
                classNames(
                  isActive ? activeLink : normalLink,
                  'group flex items-center gap-x-3 rounded-md px-3 py-2 text-sm font-semibold transition-colors'
                )
              }
            >
              <item.icon className="h-6 w-6 shrink-0" aria-hidden="true" />
              <span className={`${isSidebarOpen ? 'block' : 'hidden'}`}>{item.name}</span>
            </NavLink>
          ))}
        </nav>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Topbar */}
        <header className="bg-white shadow-sm px-6 py-4 flex justify-between items-center border-b">
          <div className="flex items-center space-x-4">
            {/* Sidebar Toggle */}
            <button 
              onClick={() => setIsSidebarOpen(!isSidebarOpen)} 
              className="text-blue-600 focus:outline-none p-2 rounded-lg hover:bg-blue-50 transition-colors"
              title={isSidebarOpen ? "Collapse Sidebar" : "Expand Sidebar"}
            >
              {isSidebarOpen ? <XMarkIcon className="h-5 w-5" /> : <Bars3Icon className="h-5 w-5" />}
            </button>
            
            {/* Back control — every detail page, not just customers */}
            {back && (
              <button
                onClick={() => navigate(back.href)}
                className="flex items-center space-x-2 text-blue-600 hover:text-blue-800 focus:outline-none p-2 rounded-lg hover:bg-blue-50 transition-colors"
                title={back.label}
              >
                <ArrowLeftIcon className="h-5 w-5" />
                <span className="text-sm font-medium">{back.label}</span>
              </button>
            )}
          </div>

          <h2 className="text-2xl font-bold text-gray-800">{title}</h2>

          <div className="flex items-center space-x-4">
            <NotificationPanel />
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                aria-haspopup="menu"
                aria-expanded={dropdownOpen}
                className="flex items-center space-x-2 focus:outline-none"
              >
                <UserCircleIcon className="h-7 w-7 text-blue-600" />
                <span className="text-gray-800 font-medium">{user?.email}</span>
              </button>
              {dropdownOpen && (
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-20 border">
                  <NavLink to="/profile" className="block px-4 py-2 text-sm text-gray-700 hover:bg-blue-50">Profile</NavLink>
                  <button onClick={handleLogout} className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-blue-50">
                    Logout
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-x-hidden overflow-y-auto bg-gray-100 p-6">
          {children ? children : null}
        </main>
        {/* Footer */}
        <footer className="bg-white shadow-md p-4 text-center text-sm text-gray-600 border-t">
          © {new Date().getFullYear()} {COMPANY.name}. All rights reserved.
          <p className="mt-1">Contact: <a href={`mailto:${COMPANY.email}`} className="text-blue-600 hover:underline">{COMPANY.email}</a></p>
        </footer>
      </div>
    </div>
  )
}

export default AdminLayout 